import EventEmitter from 'wolfy87-eventemitter';
import { inject } from 'inversify';
import { GroupedAnnouncements, ICategoryAnnouncement } from '../../../types/announcements';
import { announcementsSources } from '../../../sources/announcements';
import { ANNOUNCEMENTS_MODULE_IDS } from './announcements.const';
import messager from '../../messager';
import MESSAGE_TYPES from '../../../constants/MESSAGE_TYPES';
import type { IAnnouncementEvents } from './announcements.events';
import groupBy from 'lodash/groupBy';
import { TICKETS_EVENTS } from '../../../constants/CONTROLLERS_EVENT_TYPES';
import type { IAnnouncementsController } from './announcements.types';
import { ANNOUNCEMENTS_TYPE, SORT_ON } from '../../../constants/ANNOUNCEMENT';
import { isEmpty } from 'lodash';

export default class AnnouncementsController
  extends EventEmitter
  implements IAnnouncementsController
{
  announcements: GroupedAnnouncements;
  count: number = 0;
  filters: { [key: string]: boolean } = { all: true };
  selectedAnnouncements: { [key: string]: number } = {};
  announcementsGroup: { [key: string]: { [key: string]: ICategoryAnnouncement[] } };

  constructor(
    @inject(ANNOUNCEMENTS_MODULE_IDS.ANNOUNCEMENTS_EVENTS) public events: IAnnouncementEvents,
  ) {
    super();
  }

  loadAnnouncements = async () => {
    try {
      let data = await announcementsSources.loadAnnouncements();
      const { selectedTickets } = window.Widget.settings;

      if (selectedTickets?.length) {
        data = data.map((announcement) => {
          const selectedAnnouncement = selectedTickets.find(
            (ticket) => ticket.id === announcement.id,
          );

          if (selectedAnnouncement) {
            return {
              ...announcement,
              selectedCount: selectedAnnouncement.count,
            };
          }

          return announcement;
        });
      }

      this.announcements = groupBy(data, 'view_scope_type') as GroupedAnnouncements;
      this.announcementsGroup = {
        [ANNOUNCEMENTS_TYPE.CATEGORY]: groupBy(
          this.announcements['CATEGORY'],
          (item) => item.fragment.attributes.label,
        ),
        [ANNOUNCEMENTS_TYPE.SECTOR]: groupBy(
          this.announcements['SECTOR'],
          (item) => item.fragment.attributes.label,
        ),
        [ANNOUNCEMENTS_TYPE.ROW]: this.announcements['ROW'],
      } as { [key: string]: { [key: string]: ICategoryAnnouncement[] } };

      this.filters.sector =
        this.announcements['SECTOR'] && this.announcements['SECTOR']?.length > 0;
      this.filters.row = this.announcements['ROW'] && this.announcements['ROW']?.length > 0;
      this.filters.category =
        this.announcements['CATEGORY'] && this.announcements['CATEGORY']?.length > 0;
      this.count = data.length;
    } catch (error) {
      this.count = 0;
    }
  };

  addAnnouncement(announcement, count) {
    this.selectedAnnouncements[announcement.id] = count;

    const { selectedEvent, meta } = window.Widget.metaController;
    this.emit(this.events.SELECT_ANNOUNCEMENT, { id: announcement.id, newCount: count });

    messager.send({
      type: MESSAGE_TYPES.ON_ADD_ANNOUNCEMENT,
      announcement: {
        ...announcement,
        selectedEvent: meta.isPeriodical ? selectedEvent : undefined,
      },
      count,
    });
  }

  unselect() {
    this.selectedAnnouncements = {};
    this.emit(TICKETS_EVENTS.CLEAR_QUANTITY_ANNOUNCEMENT);
  }

  deleteAnnouncement(announcement, count) {
    if (count === 0) {
      delete this.selectedAnnouncements[announcement.id];
    }

    const { selectedEvent, meta } = window.Widget.metaController;
    this.emit(this.events.SELECT_ANNOUNCEMENT, { id: announcement.id, newCount: count });

    messager.send({
      type: MESSAGE_TYPES.ON_DELETE_ANNOUNCEMENT,
      announcement: {
        ...announcement,
        selectedEvent: meta.isPeriodical ? selectedEvent : undefined,
      },
      count,
    });
  }

  reset() {
    this.count = 0;
    this.selectedAnnouncements = {};
    this.announcements = null;
  }

  getFilterAnnouncements(type, selectedFilter, selectedSubFilter, sortOn) {
    const announcementKey = type.toUpperCase();
    const isSelectAllFilter =
      selectedFilter === ANNOUNCEMENTS_TYPE.ALL && this.announcements[announcementKey];
    const isSelectAllSubFilter =
      selectedFilter === type && selectedSubFilter === ANNOUNCEMENTS_TYPE.ALL;
    const isOtherSelected = selectedFilter === type && selectedSubFilter !== ANNOUNCEMENTS_TYPE.ALL;

    const announcementsData = () => {
      if (isEmpty(this.announcements[announcementKey])) {
        return [];
      }
      if (isSelectAllFilter) {
        return this.announcements[announcementKey];
      }
      if (isOtherSelected) {
        return this.announcementsGroup[selectedFilter][selectedSubFilter];
      } else {
        return this.announcements[announcementKey];
      }
    };

    const sortedAnnouncements = [...announcementsData()].sort((a, b) => {
      if (sortOn === SORT_ON.ASC) {
        return a.price - b.price;
      } else if (sortOn === SORT_ON.DESC) {
        return b.price - a.price;
      }
      return 0;
    });

    return {
      isSelectAllFilter,
      isSelectAllSubFilter,
      isOtherSelected,
      announcementsData: sortedAnnouncements,
    };
  }
}
